import { Image, Stack, Text } from "@mantine/core";
import liff from "@line/liff";

import { getAssetUrl } from "@utils/getAssetUrl";
import { LiffModule } from "@/apps/modules";
import { CampaignButton } from "../components";
import { useLocale } from "@/apps/@components";
import { useWallpaperFlow } from "./WallpaperFlowContext";
import { logWallpaperEvent, logWallpaperPage } from "../wallpaperLog";
import { useEffect } from "react";
import { getStoredCountry, getStoredFrom } from "../wallpaperParams";

const WEB_CAMPAIGN_URL = `${process.env.REACT_APP_URL}/wallpaper/home`;

function buildCampaignUrl(locale: string): string {
	const params = new URLSearchParams();
	if (locale === "en") params.set("lang", "en");
	// 原本 url 有帶 country 就一併補回
	const country = getStoredCountry();
	if (country) params.set("country", country);
	const query = params.toString();
	return query ? `${WEB_CAMPAIGN_URL}?${query}` : WEB_CAMPAIGN_URL;
}
/** LIFF flex 按鈕 / 圖片導向：導回本專案的桌布 LIFF 入口。 */
const LIFF_CAMPAIGN_URL = `${process.env.REACT_APP_LIFF_URL}/wallpaper`;

/**
 * 是否在 LINE App 內開啟。
 * 用 userAgent 判斷（不需 liff.init 就能判），比 liff.isInClient() 穩健——
 * isInClient() 需先 init，wallpaper 頁在 LoginGuard 外、不保證 init 完成，會誤判成國際版。
 */
function isInLineApp(): boolean {
	if (typeof navigator === "undefined") return false;
	return /\bLine\//i.test(navigator.userAgent);
}

/** LIFF 分享用的 flex 訊息：文字泡泡 + 固定圖片(flex-img) + 按鈕(flex-btn)。 */
function buildShareFlexMessages(
	displayName: string,
): Parameters<typeof liff.shareTargetPicker>[0] {
	const text = `${
		displayName ? `${displayName} ` : ""
	}邀請您定制專屬 Bunny Knot 手機桌布！`;
	return [
		{ type: "text", text },
		{
			type: "flex",
			altText: text,
			contents: {
				type: "bubble",
				size: "mega",
				hero: {
					type: "image",
					url: getAssetUrl("/images/campaign-wallpaper/flex-img.png"),
					size: "full",
					aspectMode: "cover",
					aspectRatio: "220:476",
					action: {
						type: "uri",
						label: "製作專屬手機桌布",
						uri: LIFF_CAMPAIGN_URL,
					},
				},
			},
		},
		{
			type: "flex",
			altText: text,
			contents: {
				type: "bubble",
				size: "mega",
				hero: {
					type: "image",
					url: getAssetUrl("/images/campaign-wallpaper/flex-btn.png"),
					size: "full",
					aspectMode: "cover",
					aspectRatio: "220:42",
					action: {
						type: "uri",
						label: "製作專屬手機桌布",
						uri: LIFF_CAMPAIGN_URL,
					},
				},
			},
		},
	];
}

/** 國際版原生分享的文字（含活動連結與「下載桌布」= 合成圖 URL）。 */
function buildNativeShareText(locale: string, downloadUrl: string): string {
	const campaignUrl = buildCampaignUrl(locale);
	if (locale === "en") {
		return (
			`Create your personalized phone wallpaper and discover the Bunny Knot collection, unveiled on the runway and crafted with exceptional artistry.\n\n` +
			`Event Link:\n${campaignUrl}\n`
		);
	}
	// 國際中文版
	return `製作專屬您的手機桌布，並探索時裝秀亮相的 Bunny Knot 系列，感受源自精湛工藝的匠心之作:\n${campaignUrl}`;
}

export default function WallpaperSharePage() {
	const { t, locale } = useLocale();
	// 合成圖 URL 由完成頁存進流程 context（sessionStorage）→ reload 後仍讀得到、關頁自動清
	const { resultImageUrl: imageUrl } = useWallpaperFlow();

	const langSuffix = locale === "en" ? "?lang=en" : "";

	// TW（LINE 內）：走 LIFF 分享，吐文字 + flex 圖 + 按鈕
	const shareViaLiff = async () => {
		try {
			// wallpaper 頁在 LoginGuard 外，這裡自己確保 liff 已 init（冪等）
			const liffId = process.env.REACT_APP_LIFF_ID;
			if (!liffId) {
				console.error("[wallpaper-share] 缺少 REACT_APP_LIFF_ID");
				return;
			}
			await liff.init({ liffId });
			if (!liff.isLoggedIn()) {
				liff.login({ redirectUri: window.location.href });
				return;
			}
			let displayName = "";
			try {
				displayName = (await liff.getProfile()).displayName;
			} catch {
				// 拿不到名字就用空字串，不阻斷分享
			}
			const messages = buildShareFlexMessages(displayName);
			const isShared = await LiffModule.sendFlexMessages(messages, false);

			if (isShared) {
				logWallpaperEvent({
					category: "cta",
					path: "/wallpaper/share_success",
					action: "success",
					event: "cta_share",
				});
			}
		} catch (error) {
			console.error("[wallpaper-share] liff share failed", error);
		}
	};

	// 國際版（一般瀏覽器）：叫出手機預設分享，分享固定文字
	const shareViaNative = async () => {
		// 「下載桌布」連結指向我們自己排版的檢視頁（圖置中、留白），
		// 而不是原始圖片 URL（會被瀏覽器縮放到滿版）
		const downloadUrl = imageUrl
			? `${process.env.REACT_APP_URL}/wallpaper/view?img=${encodeURIComponent(
					imageUrl,
				)}`
			: buildCampaignUrl(locale);
		const text = buildNativeShareText(locale, downloadUrl);
		if (typeof navigator !== "undefined" && navigator.share) {
			try {
				await navigator.share({ text });
			} catch {
				// 使用者取消分享，忽略
			}
			return;
		}
		// 不支援原生分享（例如桌面）→ 複製到剪貼簿
		try {
			await navigator.clipboard.writeText(text);
		} catch (error) {
			console.error("[wallpaper-share] clipboard failed", error);
		}
	};

	const handleShare = () => {
		if (isInLineApp()) {
			shareViaLiff();

			logWallpaperEvent({
				category: "cta",
				path: "/wallpaper/share",
				action: "share",
				event: "cta_share",
			});
		} else {
			shareViaNative();
		}
	};

	useEffect(() => {
		logWallpaperPage({
			path: `/wallpaper/share${langSuffix}`,
			from: getStoredFrom(),
		});
	}, []);

	return (
		<Stack
			h="100svh"
			w="100%"
			gap={0}
			style={{
				backgroundImage: `url(${getAssetUrl(
					"/images/campaign-wallpaper/share-bg.png",
				)})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
			p="7.6vw"
			align="center"
			pos="relative"
		>
			<Image
				src={getAssetUrl("/images/campaign-wallpaper/logo-white.svg")}
				alt="wallpaper-home"
				w="48.8vw"
			/>
			<Stack
				align="center"
				gap="2vw"
				pos="absolute"
				bottom="9.7vw"
				left="50%"
				w="100%"
				style={{ transform: "translateX(-50%)" }}
			>
				<Text
					fw={900}
					fz={locale === "en" ? "4vw" : "4.5vw"}
					c="white"
					ta="center"
					style={{ letterSpacing: locale === "en" ? "-0.04em" : "0.1em" }}
				>
					{t("share.title")}
				</Text>
				<Text
					fw={500}
					fz={locale === "en" ? "3.5vw" : "4.5vw"}
					c="white"
					ta="center"
					style={{ letterSpacing: locale === "en" ? "-0.04em" : "0.05em" }}
				>
					{t("share.description")}
				</Text>
				<CampaignButton
					color="white"
					style={{ borderWidth: "1px", letterSpacing: locale === "en" ? "0.06em" : "0.2em" }}
					mt="2.5vw"
					w="39.1vw"
					onClick={handleShare}
          fz={locale === "en" ? "3.3vw" : "4vw"}
          lh="normal"
				>
					{t("share.btn")}
				</CampaignButton>
			</Stack>
		</Stack>
	);
}
